import {
    BrandModel,
    MetadataTypeContract,
    MetadataTypesContract,
    TechnicalProductionTypeModel,
    TextTypeModel,
    ThemaSearchContractSearchResultItemContract as ThemaSearchResultItem,
} from 'ApiClient/metadata'
import { z } from 'zod'

import { servicesAsync } from 'Common/environment/environment'
import { MetadataItem } from 'Common/metadata'
import { fetchDelete, fetchGet, fetchPatch, fetchPost } from 'Common/utils/query/request'

const baseUrl = servicesAsync.metadata('/v2/merge?')
const newBaseUrl = servicesAsync.metadata('/v1/metadata?')
const searchUrl = servicesAsync.metadata('/v2/search/thema?')
const typesUrl = servicesAsync.metadata('/v2/types')

export const baseMetadataItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    isActive: z.boolean(),
})

export type BaseMetadataItem = z.infer<typeof baseMetadataItemSchema>

export const metadataTypedSchema = z.record(z.string(), z.array(baseMetadataItemSchema))

export type MetadataTyped = z.infer<typeof metadataTypedSchema>

interface SearchThemaRequest {
    languageCode: string
    parentCodeId?: string | null
    themaCodeTypeId: string
    query: string
}

export const searchThema = async (request: SearchThemaRequest) => {
    const languageFilter = `$filter=languageCode eq '${request.languageCode}'`
    let parentFilter = ''
    if (request.query !== '') {
        // No parentFilter needed when query is present
    } else if (request.parentCodeId !== null) {
        parentFilter = ` and codeParent eq '${request.parentCodeId}'`
    } else {
        parentFilter = ` and codeParent eq null`
    }
    const filter = ` and themaCodeTypeId eq '${request.themaCodeTypeId}'`
    const query = request.query ? `q=${request.query}&` : ''
    const response = await fetchGet(
        `${await searchUrl}${query}${languageFilter}${encodeURIComponent(parentFilter)}${encodeURIComponent(filter)}&$orderby=codeValue&limit=200`,
    )
    return response.results as ThemaSearchResultItem[]
}

export const metadataServiceBundleGet = async (
    names: readonly string[],
): Promise<Record<string, unknown[]>> => {
    const result = await fetchGet(`${await newBaseUrl}filter=${names.join(',')}`)
    return (result ?? {}) as Record<string, unknown[]>
}

export const claimValuesGet = async () => {
    return await fetchGet(
        `${await baseUrl}filter=claimValues_opus_asset,claimValues_opus_concept,claimValues_opus_contacts,claimValues_opus_work&refreshcache=true`,
    )
}

export const brandsGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=brands`)
    return result.brands as BrandModel[]
}

export const textTypesGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=textTypes`)
    return result.textTypes as TextTypeModel[]
}

export const technicalProductionTypesGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=technicalProductionTypes`)
    return result.technicalProductionTypes as TechnicalProductionTypeModel[]
}

export const bookClubsGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=bookClubs`)
    return result.bookClubs ?? ([] as MetadataItem[])
}

export const bookClubsMaterialsGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=bookClubMaterials`)
    return result.bookClubMaterials ?? ([] as MetadataItem[])
}

export const coverMetadataGet = async () => {
    const coverTypes = ['coverPapers', 'jacketPapers', 'printedCoverPapers', 'softCoverPapers']

    const result = await fetchGet(
        `${await newBaseUrl}filter=${coverTypes.join(',')},printings,laminations,coverPlacements`,
    )
    return result as MetadataTyped
}

export const coverPapersGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=coverPapers`)
    return (result.coverPapers ?? []) as MetadataItem[]
}

export const activityStatusGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=activityStatus`)
    return (result.activityStatus ?? []) as MetadataItem[]
}

export const activitySharingLevelsGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=activitySharingLevels`)
    return (result.activitySharingLevels ?? []) as MetadataItem[]
}

export const contactAddressTypesGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=contactAddressTypes`)
    return result.contactAddressTypes!
}

export const linkTypesGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=linkTypes`)
    return result.linkTypes!
}

export const awardsGet = async () => {
    const result = await fetchGet(`${await newBaseUrl}filter=awards`)
    return result.awards!
}

export const metadataTypesGet = async () => {
    const result = (await fetchGet(`${await typesUrl}`)) as MetadataTypesContract
    return result.types ?? []
}

export const metadataTypeGet = async (id: string) => {
    return (await fetchGet(`${await typesUrl}/${id}`)) as MetadataTypeContract
}

interface MetadataRequest {
    typeId: number
    values?: any
    id?: number
    metadataItemId?: number
}

export const metadataItemPost = async (request: MetadataRequest) => {
    return await fetchPost(
        `${await typesUrl}/${request.typeId}/items`,
        JSON.stringify(request.values),
    )
}

export const metadataItemPatch = async (request: MetadataRequest) => {
    return await fetchPatch(
        `${await typesUrl}/${request.typeId}/items/${request.id}`,
        JSON.stringify(request.values),
    )
}

export const metadataItemDelete = async (request: MetadataRequest) => {
    return await fetchDelete(`${await typesUrl}/${request.typeId}/items/${request.id}`)
}

export const customPropertyTypePost = async (request: MetadataRequest) => {
    return await fetchPost(
        `${await typesUrl}/${request.typeId}/props`,
        JSON.stringify(request.values),
    )
}

export const customPropertyItemPost = async (request: MetadataRequest) => {
    return await fetchPost(
        `${await typesUrl}/${request.typeId}/items/${request.metadataItemId}/props`,
        JSON.stringify(request.values),
    )
}

export const customPropertyItemPatch = async (request: MetadataRequest) => {
    return await fetchPatch(
        `${await typesUrl}/${request.typeId}/items/${request.metadataItemId}/props/${request.id}`,
        JSON.stringify(request.values),
    )
}

export const customPropertyItemDelete = async (request: MetadataRequest) => {
    return await fetchDelete(
        `${await typesUrl}/${request.typeId}/items/${request.metadataItemId}/props/${request.id}`,
    )
}
